import { useCallback, type FC } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/flexkit/Button";
import { WorkflowEditTriggerItem } from "@/components/automations/AutomationEditTriggerItem";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerData,
} from "@/components/automations/automation-edit-form-data";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainWorkflow } from "@/queries/automation-queries";

type WorkflowEditTriggersProps = {
    workflow: PlainWorkflow;
    triggers: WorkflowEditFormTriggerData[];
    onTriggersChange: (triggers: WorkflowEditFormTriggerData[]) => void;
};

export const WorkflowEditTriggers: FC<WorkflowEditTriggersProps> = ({ workflow, triggers, onTriggersChange }) => {
    const handleAddTrigger = useCallback(() => {
        const newTrigger: WorkflowEditFormTriggerData = {
            id: `trigger-${uuidv4()}`,
            type: "manual",
            config: {},
            context: { type: WorkflowEditFormTriggerContextType.Projects, projectIds: [] },
        };
        onTriggersChange([...triggers, newTrigger]);
    }, [triggers, onTriggersChange]);

    const handleUpdateTrigger = useCallback(
        (triggerId: string, updatedTrigger: WorkflowEditFormTriggerData) => {
            onTriggersChange(triggers.map((trigger) => (trigger.id === triggerId ? updatedTrigger : trigger)));
        },
        [triggers, onTriggersChange],
    );

    const handleRemoveTrigger = useCallback(
        (triggerId: string) => {
            onTriggersChange(triggers.filter((trigger) => trigger.id !== triggerId));
        },
        [triggers, onTriggersChange],
    );

    return (
        <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
                <Heading2 className="text-md font-medium -tracking-[1px] text-content-primary">Trigger</Heading2>
                <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={handleAddTrigger}
                    disabled={triggers.length > 0}
                    LeadingIcon={IconPlus}
                    data-tracking-id="add-trigger-workflow-edit-triggers"
                >
                    New Trigger
                </Button>
            </div>

            {triggers.length === 0 ? (
                <Text className="text-sm text-content-destructive">At least one trigger is required.</Text>
            ) : (
                <div className="flex flex-col gap-3">
                    {triggers.map((trigger) => (
                        <WorkflowEditTriggerItem
                            key={trigger.id}
                            workflow={workflow}
                            trigger={trigger}
                            onUpdate={(updatedTrigger) => handleUpdateTrigger(trigger.id, updatedTrigger)}
                            onRemove={() => handleRemoveTrigger(trigger.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
